import React, { useEffect, useState } from "react";
import { NOT_APPLICABLE, DEBOUNCED_DURATION } from "constants/AppConstants";
import { Input, Icons, Alert, BasePopup } from "@texada/ui";
import { useDebounce } from "use-debounce";
import { toNumber, isNumber, isEmpty, debounce } from "lodash";
import {
  isValidNumber,
  SrmBoolean,
  ASSET_TYPE,
  CurrencyFormat,
  ASSET_STATUS,
  DOC_STATUS_CODE
} from "helpers/helper";
import Big from "big.js";
import ReactLoading from "@texada/ui/src/components/loading/ReactLoading";
import i18n from "i18n/i18n";
import SimpleTooltip from "components/commons/simpleTooltip/SimpleTooltip";
import { isReadOnlyService } from "../ProductServiceUtils";

enum QUANTITY {
  ZERO = 0
}

export const AvailabilityRenderer = ({
  item,
  onOpenCalendar,
  showLoading = true
}) => {
  const [isLoading, setIsLoading] = useState(showLoading);
  const [quantityOrStatus, setQuantityOrStatus] = useState("");
  const [color, setColor] = useState("");

  useEffect(() => {
    if (item.availability) {
      setIsLoading(false);
    }
  }, [item.availability]);

  useEffect(() => {
    let quantity = isEmpty(item?.availability)
      ? NOT_APPLICABLE
      : item.availability.quantity;
    const isQuantityLessThanOne = parseInt(quantity) <= 0;
    const isQuantityNumber = !isNaN(quantity);
    let color = isQuantityNumber
      ? isQuantityLessThanOne
        ? "red"
        : "green"
      : "";

    if (item?.availability?.availability === SrmBoolean.False) {
      quantity = i18n.t("available");
      color = "green";
    }

    setColor(color);

    if (item?.asset_document_status?.length > 0) {
      const allStatuses = item.asset_document_status
        .map(e => e.status_code)
        .join(",")
        .split(",");
      const prioritizedStatus = [
        DOC_STATUS_CODE.ON_DELIVERY,
        DOC_STATUS_CODE.ON_CLOSED_PICKUP,
        DOC_STATUS_CODE.ON_PICKUP,
        DOC_STATUS_CODE.ON_PICKUP_EXCHANGE,
        DOC_STATUS_CODE.ON_CONTRACT,
        DOC_STATUS_CODE.ON_SAVED_WORKSHEET,
        DOC_STATUS_CODE.ON_RESERVATION,
        DOC_STATUS_CODE.OFF_RENTAL,
        DOC_STATUS_CODE.EXCHANGED,
        DOC_STATUS_CODE.ON_WORKORDER,
        DOC_STATUS_CODE.INVENTORY_TRANSFER,
        DOC_STATUS_CODE.ON_SALES_ORDER,
        DOC_STATUS_CODE.ON_LEASE
      ].find(status => allStatuses.includes(status));
      if (prioritizedStatus) {
        setColor("red");
      }
      setQuantityOrStatus(
        prioritizedStatus ? prioritizedStatus : allStatuses[0]
      );
      return;
    }

    if (item?.statusCode) {
      setQuantityOrStatus(item.statusCode);
      return;
    }

    setQuantityOrStatus(quantity);
  }, [item]);

  if (isLoading) {
    return (
      <ReactLoading
        className="rate-loader"
        type="spokes"
        color="black"
        width={18}
        height={18}
        delay={200}
      />
    );
  }

  return (
    <>
      <div className="contract-available">
        <span className={`available-color ${color}`}>{quantityOrStatus}</span>
        {[item.type, item.assetOrPart].includes(ASSET_TYPE.Rental) && (
          <span onClick={onOpenCalendar} style={{ marginLeft: 15 }}>
            <Icons.CalendarTodayBlack />
          </span>
        )}
      </div>
    </>
  );
};

export const QuantityRenderer: React.FC<{
  item: Models.ContractDetail;
  readonly?: boolean;
  changeQuantity?: Function;
}> = props => {
  const { item: product, readonly, changeQuantity } = props;
  const { quantity } = product;
  const isNonBulkItem = product?.bulkItem === SrmBoolean.False;
  const [showMessage, setShowMessage] = useState(false);
  const [value, setValue] = useState(quantity);
  const [debouncedValue] = useDebounce(
    value,
    DEBOUNCED_DURATION.FIFTEEN_HUNDRED
  );

  const onUpdate = debounce(async () => {
    const updated = await changeQuantity(product, debouncedValue);
    if (!updated) {
      setValue(quantity);
    }
  }, 250);

  useEffect(() => {
    setValue(quantity);
  }, [quantity]);

  useEffect(() => {
    if (String(debouncedValue) === String(quantity)) return;
    if (String(debouncedValue) === String(QUANTITY.ZERO)) {
      Alert.error({
        message: i18n.t(
          "quantity_cannot_be_zero_or_negative_it_is_defaulted_to_one"
        )
      });
      setValue(quantity);
      return;
    }
    onUpdate();
  }, [debouncedValue]);

  let errorMessage;
  if (!isValidNumber(quantity)) {
    errorMessage = "Invalid quantity";
  } else if (toNumber(quantity) <= 0) {
    errorMessage = "Quantity cannot be 0 or negative";
  } else if (isNonBulkItem && toNumber(quantity) > 1) {
    errorMessage = "Quantity can not be more than 1 on a bulk item";
  }

  return (
    <BasePopup
      trigger={
        <Input
          type="number"
          numberControl
          value={value}
          decimalPlaces={product.type === ASSET_TYPE.Rental ? 0 : 2}
          onChange={e => setValue(Number(e.target.value))}
          error={errorMessage !== undefined}
          readOnly={
            readonly || (isNonBulkItem && toNumber(product.quantity) === 1)
          }
          inputProps={{
            min: "1",
            onKeyDown: e => {
              if (e.key === ".") {
                e.preventDefault();
                e.stopPropagation();
              }
            }
          }}
        />
      }
      content={errorMessage}
      open={showMessage && errorMessage !== undefined}
      on="focus"
      onClose={() => setShowMessage(false)}
      onOpen={() => setShowMessage(true)}
      inverted
      position="top left"
    />
  );
};

function formatCost(cost) {
  const num = Number(cost);
  return Number.isFinite(num) ? num.toFixed(2) : "";
}

export const ContractRateRenderer: React.FC<{
  item: Models.ContractDetail;
  readonly?: boolean;
  changeRate?: Function;
}> = ({ item: product, readonly = false, changeRate }) => {
  const { rate, listPrice, type } = product;
  const [showMessage, setShowMessage] = useState(false);

  if (rate === undefined && listPrice === undefined) {
    return (
      <ReactLoading
        className="rate-loader"
        type="spokes"
        color="black"
        width={18}
        height={18}
        delay={200}
      />
    );
  }

  let errorMessage;
  if (!isValidNumber(rate)) {
    errorMessage = "Invalid rate";
  } else if (Big(rate).s === -1) {
    errorMessage = "Rate cannot be negative";
  }

  const canEditRate =
    !readonly &&
    product.type === ASSET_TYPE.Rental &&
    product.hasOwnProperty("rate");

  const cost =
    !isEmpty(product?.detailTrxN) || product.type === ASSET_TYPE.Sales
      ? listPrice
      : rate || listPrice;

  return (
    <div>
      <BasePopup
        trigger={
          <Input
            type="number"
            unit={{ label: "$", position: "left" }}
            decimalPlaces={2}
            value={canEditRate ? formatCost(cost) : Number(cost)}
            onChange={e => changeRate(product.index, e.target.value)}
            readOnly
            inputProps={{
              min: "0"
            }}
          />
        }
        content={errorMessage}
        open={showMessage && errorMessage !== undefined}
        on="focus"
        onClose={() => setShowMessage(false)}
        onOpen={() => setShowMessage(true)}
        inverted
        position="top left"
      />
    </div>
  );
};

export const UnitCostRenderer = ({ item, onChange, readonly, isCreate }) => {
  const [price, setPrice] = useState(item.listPrice);

  useEffect(() => {
    setPrice(item.listPrice);
  }, [item.listPrice]);

  if (item.type === ASSET_TYPE.Sales) {
    return CurrencyFormat.format(
      isNumber(item.amount) ? item.amount : item.listPrice
    );
  }

  return (
    <Input
      type="number"
      unit={{ label: "$", position: "left" }}
      decimalPlaces={2}
      value={price}
      onChange={e => setPrice(Number(e.target.value))}
      onBlur={() => {
        onChange(item, price)
      }}
      inputProps={{
        min: "0"
      }}
      readOnly={readonly || isReadOnlyService(item, isCreate)}
    />
  );
};
export function useGetRateHeaders(division, customer) {
  return (product: Models.ContractDetail) => ({
    fromDate: product.dateOut,
    fromTime: product.timeOut,
    toDate: product.dateIn,
    toTime: product.timeIn,
    division,
    customer: customer || ""
  });
}

export const ProductDayRateRenderer: React.FC<{
  item: any;
  rates: any;
}> = ({ item, rates }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [rate, setRate] = useState(0);

  useEffect(() => {
    if (rates.hasOwnProperty(item.product)) {
      const dayRate = rates[item.product]?.rates?.find(e =>
        e.description.toLowerCase().includes("daily")
      );
      setRate(dayRate ? dayRate.rate : rates[item.product].standardRate);
      setIsLoading(false);
    }
  }, [item.product, rates]);

  if (isLoading) {
    return (
      <ReactLoading
        className="rate-loader"
        type="spokes"
        color="black"
        width={18}
        height={18}
        delay={200}
      />
    );
  }
  return <span>{CurrencyFormat.format(rate)}</span>;
};

export const ProductFullRateRenderer: React.FC<{
  item: any;
  rates: any;
}> = ({ item, rates }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [rate, setRate] = useState(0);
  const [rateArray, setRateArray] = useState({});

  useEffect(() => {
    if (rates.hasOwnProperty(item.product)) {
      if (!isEmpty(rates[item.product]?.rates)) {
        setRateArray(rates[item.product]?.rates);
      }
      setRate(rates[item.product]?.standardRate);
      setIsLoading(false);
    }
  }, [item.product, rates]);

  if (isLoading) {
    return (
      <ReactLoading
        className="rate-loader"
        type="spokes"
        color="black"
        width={18}
        height={18}
        delay={200}
      />
    );
  }
  if (!isEmpty(rateArray)) {
    return (
      <>
        {rateArray.map((e, index) => (
          <span>
            {e.rate ? CurrencyFormat.format(e.rate) : ""}
            {index !== rateArray.length - 1 ? " | " : ""}
          </span>
        ))}
      </>
    );
  }
  return <span>{rate ? rate : CurrencyFormat.format(rate)}</span>;
};

export const LinkRenderer: React.FC<{ item: any }> = ({ item }) => {
  return (
    <div className="contract-product-link" tabIndex="0" role="button">
      <Icons.InsertLink />
    </div>
  );
};

export const ProductRenderer: React.FC<{ item: any }> = ({ item }) => {
  return (
    <div className="contract-product-number">
      <span
        role="button"
        tabIndex="0"
        onClick={() =>
          window.open(
            `/inventory/${
              item.type === ASSET_TYPE.Rental ? "asset" : "part"
            }/${item.product.trim()}`,
            "_blank"
          )
        }
        className="c"
      >
        {item.product}
      </span>
      {(item?.exchanges?.length > 0 ||
        !isEmpty(item?.substitution?.requestedProductN)) && (
        <SimpleTooltip
          debug={true}
          render={({ item, Container, Bold, Content, Grey }) => {
            return (
              <>
                {item?.exchanges?.length > 0 && (
                  <Container>
                    <Bold>{i18n.t("exchange")}:</Bold>
                  </Container>
                )}
                {item?.exchanges?.map(e => (
                  <>
                    <Container>
                      <Bold>{i18n.t("old_product_number")}</Bold>
                      <Content>
                        <div
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {e?.oldProduct?.trim()}
                        </div>
                      </Content>
                      <Grey />
                    </Container>
                    <Container>
                      <Bold>{i18n.t("old_product_description")}</Bold>
                      <Content>
                        <div
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {e?.oldDescription?.trim()}
                        </div>
                      </Content>
                      <Grey />
                    </Container>
                  </>
                ))}
                {!isEmpty(item?.substitution?.requestedProductN) && (
                  <>
                    <Container>
                      <Bold>{i18n.t("substitution")}:</Bold>
                    </Container>
                    <Container>
                      <Bold>{i18n.t("requested_product_number")}</Bold>
                      <Content>
                        <div
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {item.substitution.requestedProductN.trim()}
                        </div>
                      </Content>
                      <Grey />
                    </Container>
                    <Container>
                      <Bold>{i18n.t("rented_product_number")}</Bold>
                      <Content>
                        <div
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {item?.substitution?.rentedProductN?.trim() ??
                            item?.substitution?.rentedProduct?.trim()}
                        </div>
                      </Content>
                      <Grey />
                    </Container>
                  </>
                )}
              </>
            );
          }}
          item={item}
          onHover={() => {}}
          style={{
            display: "flex",
            marginTop: 2
          }}
        >
          <Icons.SwapHorizontalCircle />
        </SimpleTooltip>
      )}
    </div>
  );
};

export const StatusRenderer: React.FC<{ item: any }> = ({ item }) => {
  const [status, setStatus] = useState("");

  useEffect(() => {
    switch (item.status) {
      case ASSET_STATUS.OnRent:
        setStatus(i18n.t("on_rent"));
        return;
      case ASSET_STATUS.OffRent:
        setStatus(i18n.t("off_rent"));
        return;
      case ASSET_STATUS.Sold:
        setStatus(i18n.t("sold"));
        return;
      case ASSET_STATUS.Returned:
        setStatus(i18n.t("returned"));
        return;
      default:
        setStatus("");
    }
  }, [item.product, item.status]);

  return (
    <span
      className={`contract-product-status ${status
        .toLowerCase()
        .replace(" ", "_")}`}
    >
      {status}
    </span>
  );
};

export const TotalRenderer = props => {
  const unitCost = Number(props.item.listPrice);
  const quantity = Number(props.item.quantity);

  if (isValidNumber(quantity)) {
    return CurrencyFormat.format(unitCost * quantity);
  }
  return "";
};
